/**
 * KitchenDisplay — pantalla dedicada para la tablet de cocina.
 *
 * Ruta: /kitchen/display  (sin AppShell, pantalla completa)
 * - Tarjetas grandes con botones de un toque
 * - Auto-refresh cada 5 s + WebSocket
 * - Selector de área (Cocina 1 / 2)
 * - Filtro de destino y tabs de estado
 * - Sin sidebar ni navegación superior
 */

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, ChefHat, CheckCircle2, ArrowLeft, RefreshCw, Wifi, WifiOff, MessageSquare } from 'lucide-react';

import { useOrders, useOrderStatusMutation } from '../../hooks/useOrders';
import { useProducts } from '../../hooks/useProducts';
import { useTables } from '../../hooks/useTables';
import { safeArray } from '../../utils/safe';
import { normalizeOrder, type NormalizedOrder } from '../../utils/normalize-order';
import { echo } from '../../lib/echo';

// ── Types ─────────────────────────────────────────────────────────────────────

type KitchenStatus = 'WAITING' | 'IN_PRODUCTION' | 'DONE';
type DestFilter    = 'ALL' | 'MESA' | 'TAKEAWAY';

const STATUS_META: Record<KitchenStatus, { label: string; icon: React.ElementType; accent: string; ring: string; next: string; action: string }> = {
  WAITING:       { label: 'En espera',     icon: Clock,         accent: 'border-amber-400  bg-amber-50',   ring: 'ring-amber-400',  next: 'IN_PRODUCTION', action: 'Preparar' },
  IN_PRODUCTION: { label: 'Preparando',    icon: ChefHat,       accent: 'border-blue-400   bg-blue-50',    ring: 'ring-blue-400',   next: 'DONE',          action: 'Listo ✓'  },
  DONE:          { label: '¡Listo!',       icon: CheckCircle2,  accent: 'border-emerald-400 bg-emerald-50', ring: 'ring-emerald-400', next: 'DELIVERED',     action: 'Entregar' },
};

const DEST_OPTIONS: { value: DestFilter; label: string }[] = [
  { value: 'ALL',     label: 'Todos' },
  { value: 'MESA',    label: 'Mesas' },
  { value: 'TAKEAWAY', label: 'Para llevar' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function elapsed(iso?: string): string {
  if (!iso) return '';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)  return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function urgencyClass(iso?: string): string {
  if (!iso) return '';
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins > 20) return 'text-red-600 font-bold animate-pulse';
  if (mins > 10) return 'text-amber-600 font-semibold';
  return 'text-gray-500';
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function KitchenDisplay() {
  const queryClient   = useQueryClient();
  const [activeStatus, setActiveStatus] = useState<KitchenStatus>('WAITING');
  const [activeDest,   setActiveDest]   = useState<DestFilter>('ALL');
  const [activeArea,   setActiveArea]   = useState<number>(1);
  const [connected,    setConnected]    = useState(true);
  const [lastRefresh,  setLastRefresh]  = useState(Date.now());

  const { data: rawOrders = [], refetch } = useOrders();
  const { data: products  = [] }          = useProducts();
  const { data: tables    = [] }          = useTables();
  const statusMutation                    = useOrderStatusMutation();

  // ── Real-time WebSocket ───────────────────────────────────────────────────
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      refetch();
      setLastRefresh(Date.now());
    };

    const ch1 = echo.channel('pos-updates');
    const ch2 = echo.channel('orders');
    ch1.listen('TableUpdated', invalidate);
    ch2.listen('.orders.new',     invalidate);
    ch2.listen('.orders.updated', invalidate);

    // Connection state
    echo.connector?.pusher?.connection?.bind('connected',    () => setConnected(true));
    echo.connector?.pusher?.connection?.bind('disconnected', () => setConnected(false));
    echo.connector?.pusher?.connection?.bind('failed',       () => setConnected(false));

    return () => {
      echo.leave('pos-updates');
      echo.leave('orders');
    };
  }, [refetch, queryClient]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const catalogById = useMemo(() => {
    const m: Record<string, { name: string; price: number }> = {};
    products.forEach(p => { m[String(p.id)] = { name: p.name, price: p.price }; });
    return m;
  }, [products]);

  const tablesById = useMemo(() => {
    const m: Record<string, string> = {};
    tables.forEach(t => { m[t.id] = String(t.number); });
    return m;
  }, [tables]);

  const orders = useMemo(() => rawOrders
    .map(o => normalizeOrder(o, catalogById, tablesById))
    .filter(o => ['WAITING', 'IN_PRODUCTION', 'DONE'].includes(o.status))
    .sort((a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()),
  [rawOrders, catalogById, tablesById]);

  const visibleOrders = useMemo(() => {
    return orders
      .filter(o => activeDest === 'ALL' || (o.destination ?? 'MESA') === activeDest)
      .filter(o => {
        const areaItems = o.items.filter(item => {
          const p = products.find(p => String(p.id) === String(item.id));
          return (p?.preparationArea ?? 1) === activeArea;
        });
        return areaItems.length > 0;
      })
      .map(o => ({
        ...o,
        items: o.items.filter(item => {
          const p = products.find(p => String(p.id) === String(item.id));
          return (p?.preparationArea ?? 1) === activeArea;
        }),
      }))
      .filter(o => o.status === activeStatus);
  }, [orders, activeDest, activeArea, activeStatus, products]);

  const counts = useMemo(() => {
    const filtered = orders
      .filter(o => activeDest === 'ALL' || (o.destination ?? 'MESA') === activeDest)
      .filter(o => {
        const areaItems = o.items.filter(item => {
          const p = products.find(p => String(p.id) === String(item.id));
          return (p?.preparationArea ?? 1) === activeArea;
        });
        return areaItems.length > 0;
      });
    return {
      WAITING:       filtered.filter(o => o.status === 'WAITING').length,
      IN_PRODUCTION: filtered.filter(o => o.status === 'IN_PRODUCTION').length,
      DONE:          filtered.filter(o => o.status === 'DONE').length,
    };
  }, [orders, activeDest, activeArea, products]);

  const handleAction = useCallback((order: NormalizedOrder) => {
    const meta = STATUS_META[order.status as KitchenStatus];
    if (!meta) return;
    statusMutation.mutate({ id: order.id, status: meta.next });
  }, [statusMutation]);

  return (
    <div className="flex h-dvh flex-col bg-gray-950 text-white select-none overflow-hidden">

      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <header className="flex items-center gap-3 bg-gray-900 px-4 py-3 border-b border-gray-800 shrink-0">

        {/* Back button */}
        <a
          href="#/kitchen"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-800 hover:bg-gray-700 transition"
          aria-label="Volver"
        >
          <ArrowLeft size={20} />
        </a>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold leading-tight">Pantalla Cocina</h1>
          <p className="text-xs text-gray-400 leading-none mt-0.5">
            Área {activeArea} · Actualizado{' '}
            <span className="tabular-nums">{new Date(lastRefresh).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          </p>
        </div>

        {/* Area selector */}
        <div className="flex rounded-xl overflow-hidden border border-gray-700 shrink-0">
          {[1, 2].map(a => (
            <button
              key={a}
              onClick={() => setActiveArea(a)}
              className={`px-4 py-2 text-sm font-semibold transition ${activeArea === a ? 'bg-primary text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              Cocina {a}
            </button>
          ))}
        </div>

        {/* Connection indicator */}
        <div className={`flex items-center gap-1.5 text-xs font-medium shrink-0 ${connected ? 'text-emerald-400' : 'text-red-400'}`}>
          {connected ? <Wifi size={14} /> : <WifiOff size={14} />}
          <span className="hidden sm:inline">{connected ? 'En vivo' : 'Sin conexión'}</span>
        </div>

        {/* Manual refresh */}
        <button
          onClick={() => { refetch(); setLastRefresh(Date.now()); }}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-800 hover:bg-gray-700 transition"
          aria-label="Refrescar"
        >
          <RefreshCw size={15} />
        </button>
      </header>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 bg-gray-900 px-4 py-2 border-b border-gray-800 overflow-x-auto shrink-0">
        {/* Destination filter */}
        <div className="flex rounded-lg overflow-hidden border border-gray-700 shrink-0">
          {DEST_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setActiveDest(value)}
              className={`px-3 py-1.5 text-xs font-semibold transition ${activeDest === value ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-px h-5 bg-gray-700 shrink-0" />

        {/* Status tabs */}
        {(Object.entries(STATUS_META) as [KitchenStatus, typeof STATUS_META[KitchenStatus]][]).map(([status, meta]) => {
          const Icon = meta.icon;
          const count = counts[status];
          const isActive = activeStatus === status;
          return (
            <button
              key={status}
              onClick={() => setActiveStatus(status)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition shrink-0 ${
                isActive
                  ? status === 'WAITING'       ? 'bg-amber-500 text-white'
                  : status === 'IN_PRODUCTION' ? 'bg-blue-500 text-white'
                  :                              'bg-emerald-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Icon size={15} />
              {meta.label}
              <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold ${isActive ? 'bg-black/20' : 'bg-gray-700 text-gray-300'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Orders grid ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-3 md:p-4">
        {visibleOrders.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-gray-600">
            <CheckCircle2 size={56} strokeWidth={1} />
            <p className="text-lg font-semibold">Sin órdenes en esta sección</p>
            <p className="text-sm">Las nuevas órdenes aparecerán aquí automáticamente</p>
          </div>
        ) : (
          <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleOrders.map(order => {
              const meta = STATUS_META[order.status as KitchenStatus];
              const isBusy = statusMutation.isPending && statusMutation.variables?.id === order.id;

              return (
                <div
                  key={order.id}
                  className={`flex flex-col rounded-2xl border-l-4 bg-gray-900 shadow-lg ${meta?.accent.split(' ')[0]} overflow-hidden`}
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {order.destination === 'TAKEAWAY' ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-900 px-2.5 py-0.5 text-xs font-bold text-emerald-300">
                            TAKEAWAY
                          </span>
                        ) : (
                          <span className="text-lg font-bold text-white">
                            Mesa #{order.tableLabel}
                          </span>
                        )}
                        {order.destination === 'TAKEAWAY' && order.customerName && (
                          <span className="text-base font-semibold text-white truncate max-w-[140px]">
                            {order.customerName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Orden #{order.id} · {order.items.length} ítem{order.items.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {/* Elapsed time */}
                    <span className={`text-sm tabular-nums shrink-0 mt-0.5 ${urgencyClass(order.createdAt)}`}>
                      {elapsed(order.createdAt)}
                    </span>
                  </div>

                  {/* Items list */}
                  <ul className="flex-1 divide-y divide-gray-800 overflow-y-auto max-h-56 px-4">
                    {order.items.map(item => (
                      <li key={item.id} className="py-2.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-black text-white tabular-nums">{item.qty}×</span>
                          <span className="text-base font-semibold text-gray-100 leading-snug">{item.name}</span>
                          {(item.fulfillmentType === 'TAKEOUT' || item.fulfillmentType === 'TAKEAWAY') && (
                            <span className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-bold text-blue-300 shrink-0">LLEVAR</span>
                          )}
                        </div>
                        {/* Additions / exceptions */}
                        {(item.additions || item.exceptions || item.kitchenSummary) && (
                          <div className="mt-1 flex flex-wrap gap-1 pl-8">
                            {item.kitchenSummary ? (
                              <span className="text-xs text-gray-400 italic">{item.kitchenSummary}</span>
                            ) : (
                              <>
                                {item.additions && Object.entries(item.additions).map(([k, qty]: [string, any]) =>
                                  qty > 0 ? (
                                    <span key={k} className="rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-300">
                                      +{k}{qty > 1 ? ` ×${qty}` : ''}
                                    </span>
                                  ) : null
                                )}
                                {safeArray(item.exceptions).map(k => (
                                  <span key={k} className="rounded bg-amber-900 px-2 py-0.5 text-xs text-amber-300">
                                    −{k}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  {order.note && (
                    <div className="mx-4 mb-2 flex items-start gap-2 rounded-lg bg-amber-900/40 px-3 py-2 text-sm text-amber-300">
                      <MessageSquare size={14} className="shrink-0 mt-0.5" />
                      <span>{order.note}</span>
                    </div>
                  )}

                  {/* Action button */}
                  {meta && (
                    <div className="px-4 pb-4 pt-2">
                      <button
                        onClick={() => handleAction(order)}
                        disabled={isBusy}
                        className={`w-full rounded-xl py-4 text-base font-black tracking-wide transition active:scale-95 disabled:opacity-50 ${
                          order.status === 'WAITING'       ? 'bg-amber-500 hover:bg-amber-400 text-white' :
                          order.status === 'IN_PRODUCTION' ? 'bg-blue-500  hover:bg-blue-400  text-white' :
                                                             'bg-emerald-500 hover:bg-emerald-400 text-white'
                        }`}
                      >
                        {isBusy ? '…' : meta.action}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
